/**
 * Centralized Structural Validation Utilities
 * Single source of truth for all validation logic across project
 */

export interface ValidationError {
  type: "error" | "warning";
  message: string;
  entity?: string;
  id?: number | string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

// ==================== NODE VALIDATIONS ====================

/**
 * Validates that no two nodes exist at the same location (within tolerance)
 */
export function validateNodeCoincidence(
  nodes: Array<{ id: number | string; x: number; y: number; z: number }>,
  tolerance: number = 0.001
): ValidationError[] {
  const errors: ValidationError[] = [];
  const nodeLocations = new Map<string, (number | string)[]>();

  nodes.forEach((node) => {
    const x = Math.round(node.x / tolerance) * tolerance;
    const y = Math.round(node.y / tolerance) * tolerance;
    const z = Math.round(node.z / tolerance) * tolerance;

    const locationKey = `${x.toFixed(3)},${y.toFixed(3)},${z.toFixed(3)}`;

    if (!nodeLocations.has(locationKey)) {
      nodeLocations.set(locationKey, []);
    }
    nodeLocations.get(locationKey)!.push(node.id);
  });

  nodeLocations.forEach((nodeIds, location) => {
    if (nodeIds.length > 1) {
      errors.push({
        type: "error",
        entity: "node",
        message: `Multiple nodes (${nodeIds.join(", ")}) are at the same location (${location}). Only one node can exist at each coordinate.`,
      });
    }
  });

  return errors;
}

/**
 * Validates duplicate IDs in any entity array
 */
export function validateDuplicateIds<T extends { id: number | string }>(
  items: T[],
  entityType: string
): ValidationError[] {
  const ids = items.map((item) => String(item.id));
  const duplicates = ids.filter((id, index) => ids.indexOf(id) !== index);

  if (duplicates.length > 0) {
    return [
      {
        type: "error",
        entity: entityType,
        message: `Duplicate ${entityType} IDs detected: ${[...new Set(duplicates)].join(", ")}`,
      },
    ];
  }

  return [];
}

// ==================== BEAM VALIDATIONS ====================

/**
 * Validates that no beam connects a node to itself (self-loop)
 */
export function validateBeamSelfLoop(
  beams: Array<{
    id: number | string;
    startNodeId: number | string;
    endNodeId: number | string;
  }>
): ValidationError[] {
  const errors: ValidationError[] = [];

  beams.forEach((beam) => {
    const startId = String(beam.startNodeId);
    const endId = String(beam.endNodeId);

    if (startId === endId) {
      errors.push({
        type: "error",
        entity: "beam",
        id: beam.id,
        message: `Beam ${beam.id} is a self-loop (connects node ${beam.startNodeId} to itself). Beams must connect two different nodes.`,
      });
    }
  });

  return errors;
}

/**
 * Validates that no duplicate beams exist between same two nodes
 */
export function validateDuplicateBeams(
  beams: Array<{
    id: number | string;
    startNodeId: number | string;
    endNodeId: number | string;
  }>
): ValidationError[] {
  const errors: ValidationError[] = [];
  const beamConnections = new Map<string, (number | string)[]>();

  beams.forEach((beam) => {
    // Normalize: sort node IDs to detect duplicates regardless of direction
    const key = [String(beam.startNodeId), String(beam.endNodeId)]
      .sort()
      .join("-");

    if (!beamConnections.has(key)) {
      beamConnections.set(key, []);
    }
    beamConnections.get(key)!.push(beam.id);
  });

  beamConnections.forEach((beamIds, connection) => {
    if (beamIds.length > 1) {
      const [node1, node2] = connection.split("-");
      errors.push({
        type: "error",
        entity: "beam",
        message: `Multiple beams (${beamIds.join(", ")}) connect nodes ${node1} and ${node2}. Only one beam can exist between two nodes.`,
      });
    }
  });

  return errors;
}

/**
 * Validates that beam node references exist
 */
export function validateBeamNodeReferences(
  beams: Array<{
    id: number | string;
    startNodeId: number | string;
    endNodeId: number | string;
  }>,
  nodeIds: (number | string)[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const nodeIdSet = new Set(nodeIds.map((id) => String(id)));

  beams.forEach((beam) => {
    if (!nodeIdSet.has(String(beam.startNodeId))) {
      errors.push({
        type: "error",
        entity: "beam",
        id: beam.id,
        message: `Beam ${beam.id}: start node ${beam.startNodeId} not found`,
      });
    }
    if (!nodeIdSet.has(String(beam.endNodeId))) {
      errors.push({
        type: "error",
        entity: "beam",
        id: beam.id,
        message: `Beam ${beam.id}: end node ${beam.endNodeId} not found`,
      });
    }
  });

  return errors;
}

// ==================== SUPPORT VALIDATIONS ====================

/**
 * Validates that support node references exist
 */
export function validateSupportNodeReferences(
  supports: Array<{ nodeIds: (number | string)[] }>,
  nodeIds: (number | string)[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const nodeIdSet = new Set(nodeIds.map((id) => String(id)));

  supports.forEach((support, index) => {
    support.nodeIds.forEach((nid) => {
      if (!nodeIdSet.has(String(nid))) {
        errors.push({
          type: "error",
          entity: "support",
          id: index + 1,
          message: `Support ${index + 1}: node ${nid} not found`,
        });
      }
    });
  });

  return errors;
}

/**
 * Validates that no node has multiple support assignments
 */
export function validateDuplicateSupportAssignments(
  supports: Array<{ nodeIds: (number | string)[] }>
): ValidationError[] {
  const errors: ValidationError[] = [];
  const supportNodeAssignments = new Map<string, number[]>();

  supports.forEach((support, supportIndex) => {
    support.nodeIds.forEach((nodeId) => {
      const key = String(nodeId);
      if (!supportNodeAssignments.has(key)) {
        supportNodeAssignments.set(key, []);
      }
      supportNodeAssignments.get(key)!.push(supportIndex + 1);
    });
  });

  supportNodeAssignments.forEach((supportIndices, nodeId) => {
    if (supportIndices.length > 1) {
      errors.push({
        type: "error",
        entity: "support",
        message: `Node ${nodeId} has multiple supports (Support ${supportIndices.join(", ")}). Each node can only have one support.`,
      });
    }
  });

  return errors;
}

// ==================== PROPERTY VALIDATIONS ====================

/**
 * Validates that property beam references exist
 */
export function validatePropertyBeamReferences(
  properties: Array<{ P_ID: string; beamIds: (number | string)[] }>,
  beamIds: (number | string)[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const beamIdSet = new Set(beamIds.map((id) => String(id)));

  properties.forEach((prop) => {
    prop.beamIds.forEach((bid) => {
      if (!beamIdSet.has(String(bid))) {
        errors.push({
          type: "error",
          entity: "property",
          id: prop.P_ID,
          message: `Property ${prop.P_ID}: beam ${bid} not found`,
        });
      }
    });
  });

  return errors;
}

// ==================== LOADCASE VALIDATIONS ====================

/**
 * Validates that beam loadcase references exist
 */
export function validateBeamLoadcaseReferences(
  loadcases: Array<{ name: string; targetIds: (number | string)[] }>,
  beamIds: (number | string)[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const beamIdSet = new Set(beamIds.map((id) => String(id)));

  loadcases.forEach((lc) => {
    lc.targetIds.forEach((beamId) => {
      if (!beamIdSet.has(String(beamId))) {
        errors.push({
          type: "error",
          entity: "beam_loadcase",
          message: `Beam loadcase "${lc.name}" references non-existent beam ${beamId}`,
        });
      }
    });
  });

  return errors;
}

/**
 * Validates that node loadcase references exist
 */
export function validateNodeLoadcaseReferences(
  loadcases: Array<{ name: string; targetIds: (number | string)[] }>,
  nodeIds: (number | string)[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const nodeIdSet = new Set(nodeIds.map((id) => String(id)));

  loadcases.forEach((lc) => {
    lc.targetIds.forEach((nodeId) => {
      if (!nodeIdSet.has(String(nodeId))) {
        errors.push({
          type: "error",
          entity: "node_loadcase",
          message: `Node loadcase "${lc.name}" references non-existent node ${nodeId}`,
        });
      }
    });
  });

  return errors;
}

/**
 * Validates that no entity has multiple loadcase assignments
 */
export function validateDuplicateLoadcaseAssignments(
  loadcases: Array<{ name: string; targetIds: (number | string)[] }>,
  entityType: "beam" | "node"
): ValidationError[] {
  const errors: ValidationError[] = [];
  const assignments = new Map<string, string[]>();

  loadcases.forEach((lc) => {
    lc.targetIds.forEach((targetId) => {
      const key = String(targetId);
      if (!assignments.has(key)) {
        assignments.set(key, []);
      }
      assignments.get(key)!.push(lc.name);
    });
  });

  assignments.forEach((loadcaseNames, targetId) => {
    if (loadcaseNames.length > 1) {
      errors.push({
        type: "error",
        entity: `${entityType}_loadcase`,
        message: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} ${targetId} has multiple loadcases assigned (${loadcaseNames.join(", ")}). Each ${entityType} can only have one loadcase.`,
      });
    }
  });

  return errors;
}

/**
 * Validates that no beam has multiple section property assignments
 */
export function validateDuplicateSectionAssignments(
  properties: Array<{ P_ID: string; beamIds: (number | string)[] }>,
  beamIds: (number | string)[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const beamToProperties = new Map<string, string[]>();

  properties.forEach((prop) => {
    prop.beamIds.forEach((beamId) => {
      const key = String(beamId);
      if (!beamToProperties.has(key)) {
        beamToProperties.set(key, []);
      }
      beamToProperties.get(key)!.push(prop.P_ID);
    });
  });

  beamToProperties.forEach((propertyIds, beamId) => {
    if (propertyIds.length > 1) {
      errors.push({
        type: "error",
        entity: "section_assignment",
        message: `Beam ${beamId} has multiple section properties assigned (${propertyIds.join(", ")}). Each beam can only have ONE section property.`,
      });
    }
  });

  return errors;
}

/**
 * Master validation function - validates ENTIRE model
 * Use everywhere: Terminal, Table Import, Save, Manual checks
 */
export function validateCompleteModel({
  nodes,
  beams,
  supports,
  properties,
  nodeLoadcases,
  beamLoadcases,
}: {
  nodes: Array<{ id: number | string; x: number; y: number; z: number }>;
  beams: Array<{
    id: number | string;
    startNodeId: number | string;
    endNodeId: number | string;
  }>;
  supports: Array<{ nodeIds: (number | string)[] }>;
  properties: Array<{ P_ID: string; beamIds: (number | string)[] }>;
  nodeLoadcases: Array<{ name: string; targetIds: (number | string)[] }>;
  beamLoadcases: Array<{ name: string; targetIds: (number | string)[] }>;
}): ValidationResult {
  const allErrors: ValidationError[] = [];

  // Node validations
  allErrors.push(...validateNodeCoincidence(nodes));
  allErrors.push(...validateDuplicateIds(nodes, "node"));

  // Beam validations
  allErrors.push(...validateDuplicateIds(beams, "beam"));
  allErrors.push(...validateBeamSelfLoop(beams));
  allErrors.push(...validateDuplicateBeams(beams));
  allErrors.push(
    ...validateBeamNodeReferences(
      beams,
      nodes.map((n) => n.id)
    )
  );

  // Support validations
  allErrors.push(
    ...validateSupportNodeReferences(
      supports,
      nodes.map((n) => n.id)
    )
  );
  allErrors.push(...validateDuplicateSupportAssignments(supports));

  // Property validations
  allErrors.push(
    ...validateDuplicateIds(
      properties.map((p) => ({ id: p.P_ID })),
      "property"
    )
  );
  allErrors.push(
    ...validatePropertyBeamReferences(
      properties,
      beams.map((b) => b.id)
    )
  );
  allErrors.push(
    ...validateDuplicateSectionAssignments(
      properties,
      beams.map((b) => b.id)
    )
  );

  // Loadcase validations
  allErrors.push(
    ...validateNodeLoadcaseReferences(
      nodeLoadcases,
      nodes.map((n) => n.id)
    )
  );
  allErrors.push(
    ...validateBeamLoadcaseReferences(
      beamLoadcases,
      beams.map((b) => b.id)
    )
  );
  // Note: validateDuplicateLoadcaseAssignments removed - with loadcase groups,
  // the same beam/node CAN have loads in different loadcase groups (only one active at a time)

  return {
    isValid: allErrors.length === 0,
    errors: allErrors,
  };
}
